import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, m } from 'framer-motion';
import {
  american,
  averageTotal,
  boardRows,
  formatOdds,
  marketsFor,
  payout,
} from '../../../utils/mlb-the-show/odds.js';
import { uid } from '../../../utils/mlb-the-show/format.js';
import { spring, timing } from '../motion.js';
import { Count, formatMoney, Roll, Tabs } from './primitives.js';

// Display only: no bet slip, no "place bet", no balances. Always render the disclaimer with odds.
export function OddsDisclaimer() {
  return (
    <p className="st-disc" role="note">
      <b>For entertainment only.</b> Odds are estimates from a simple rating model, may not be accurate, and do
      not predict or guarantee any result. Payouts are hypothetical play money. Nothing here places, accepts or
      facilitates a wager, and nothing here is betting or financial advice. Pull Vault is not affiliated with,
      endorsed by, or sponsored by MLB or MLB The Show.
    </p>
  );
}

function OddsCell({ cell, format, selected, onSelect }) {
  const price = american(cell.chance);
  const prevPrice = useRef(price);
  const prevChance = useRef(cell.chance);
  const [move, setMove] = useState(null);
  const direction = cell.chance > prevChance.current ? 1 : -1;

  useEffect(() => {
    if (prevPrice.current === price) return undefined;
    const up = cell.chance > prevChance.current;
    prevPrice.current = price;
    prevChance.current = cell.chance;
    setMove({ key: uid(), up });
    const t = setTimeout(() => setMove(null), timing.oddsMoveMs);
    return () => clearTimeout(t);
  }, [price, cell.chance]);

  const text = formatOdds(price, format);
  return (
    <m.button
      type="button"
      className={`st-oc${selected ? ' st-oc--on' : ''}`}
      aria-pressed={selected}
      aria-label={`${cell.description}, odds ${text}`}
      whileTap={{ scale: 0.96 }}
      onClick={() => onSelect(cell.id)}
    >
      {move && (
        <m.i
          key={move.key}
          className={`st-oc-flash ${move.up ? 'st-oc-flash--up' : 'st-oc-flash--down'}`}
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 1.4 }}
        />
      )}
      <span className="st-oc-t">
        {cell.label}
        {cell.line && <em> {cell.line}</em>}
      </span>
      <span className="st-oc-v">
        <Roll value={text} direction={direction} />
        {move && (
          <m.span
            key={`a${move.key}`}
            className={`st-oc-mv ${move.up ? 'st-oc-mv--up' : 'st-oc-mv--down'}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 1, 0] }}
            transition={{ duration: 2.3, times: [0, 0.1, 0.8, 1] }}
          >
            {move.up ? '▲' : '▼'}
          </m.span>
        )}
      </span>
    </m.button>
  );
}

function OddsBoard({ rows, format, selected, onSelect }) {
  return (
    <div className="st-odds" role="group" aria-label="Play-money odds">
      {rows.map((row) => (
        <div key={row.market} className="st-odds-row">
          <span className="st-odds-market">{row.market}</span>
          {row.cells.map((cell) => (
            <OddsCell key={cell.id} cell={cell} format={format} selected={selected === cell.id} onSelect={onSelect} />
          ))}
        </div>
      ))}
    </div>
  );
}

const AMOUNTS = [10, 25, 50, 100];

function Payout({ cell, format }) {
  const [amount, setAmount] = useState(25);
  if (!cell) return <div className="st-pay st-pr">Tap any price to see what it would pay.</div>;
  const price = american(cell.chance);
  const total = payout(amount, price);
  return (
    <div className="st-pay">
      <div className="st-pay-pick">
        <span className="st-fl">Pick</span>
        <span className="st-roll st-pay-desc">
          <AnimatePresence mode="popLayout" initial={false}>
            <m.b
              key={cell.id + cell.description}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={spring.standard}
            >
              {cell.description}
            </m.b>
          </AnimatePresence>
        </span>
        <span className="st-oc-v">{formatOdds(price, format)}</span>
      </div>
      <div className="st-pay-row">
        <span className="st-fl">Play money</span>
        {AMOUNTS.map((a) => (
          <button key={a} type="button" className="st-stake" aria-pressed={amount === a} onClick={() => setAmount(a)}>
            ${a}
          </button>
        ))}
      </div>
      <input
        type="range"
        min={1}
        max={250}
        value={amount}
        aria-label="Play-money amount"
        onChange={(e) => setAmount(Number(e.target.value))}
      />
      <div className="st-pay-out">
        <div>
          <span>Amount</span>
          <b>
            <Count value={amount} format={formatMoney} />
          </b>
        </div>
        <div>
          <span>Profit</span>
          <b>
            <Count value={total - amount} format={formatMoney} />
          </b>
        </div>
        <div>
          <span>Would pay</span>
          <b className="st-pay-big">
            <Count value={total} format={formatMoney} />
          </b>
        </div>
      </div>
      <span className="st-pr">
        Rating model gives this a{' '}
        <b>
          <Count value={Math.round(cell.chance * 100)} />%
        </b>{' '}
        chance.
      </span>
    </div>
  );
}

export function OddsPanel({ homeName, awayName, homeChance, games }) {
  const [format, setFormat] = useState('american');
  const [selected, setSelected] = useState('ml-home');
  const total = useMemo(() => averageTotal(games), [games]);
  const markets = useMemo(() => marketsFor(homeChance, total), [homeChance, total]);
  const rows = boardRows(markets, homeName, awayName);
  const cell = rows.flatMap((row) => row.cells).find((c) => c.id === selected);
  return (
    <div className="st-pane st-pane--odds">
      <div className="st-odds-head">
        <span className="st-pn">
          {awayName} at {homeName}
        </span>
        <span className="st-pr">Next game · total line from {games.length} games</span>
        <Tabs
          id="odds-format"
          label="Odds format"
          small
          options={[
            { value: 'american', label: 'American' },
            { value: 'decimal', label: 'Decimal' },
          ]}
          value={format}
          onChange={setFormat}
        />
      </div>
      <OddsBoard rows={rows} format={format} selected={selected} onSelect={setSelected} />
      <Payout cell={cell} format={format} />
      <OddsDisclaimer />
    </div>
  );
}
